// ═══════════════════════════════════════════════════════════════
// WaitlistPanel — Administration waitlist bêta (P2 Session B)
// Onglet dans UserManagement
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Mail, UserCheck, UserX, Trash2, Edit3, Clock, Inbox } from 'lucide-react';
import { confirm as confirmModal, toast } from '../ui/Toast';

const STATUS_META = {
  pending:   { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  invited:   { label: 'Invité',     color: 'bg-blue-50 text-blue-700 border-blue-200' },
  onboarded: { label: 'Onboardé',   color: 'bg-green-50 text-green-700 border-green-200' },
  declined:  { label: 'Décliné',    color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const ORIGIN_LABEL = {
  landing:  'Landing',
  referral: 'Parrainage',
  event:    'Évènement',
  manual:   'Ajout admin',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const WaitlistPanel = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [notesModal, setNotesModal] = useState(null); // { entry, value }

  const token = localStorage.getItem('oracle_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (originFilter) params.set('origin', originFilter);
      if (search.trim()) params.set('q', search.trim());
      const res = await fetch(`/api/waitlist/admin?${params.toString()}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setStats(data.stats || null);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, originFilter]);

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  const setStatus = async (id, status, notes = null) => {
    try {
      const res = await fetch(`/api/waitlist/admin/${id}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status, notes }),
      });
      if (res.ok) {
        toast.success(`Statut mis à jour : ${STATUS_META[status]?.label || status}`);
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Erreur mise à jour');
      }
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const remove = async (entry) => {
    const ok = await confirmModal(`Supprimer ${entry.email} de la waitlist ?`, { danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try {
      const res = await fetch(`/api/waitlist/admin/${entry.id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Entrée supprimée'); load(); }
      else toast.error('Erreur suppression');
    } catch (e) { toast.error('Erreur réseau'); }
  };

  const exportCsv = () => {
    const header = ['id', 'email', 'name', 'template', 'origin', 'status', 'created_at', 'invited_at', 'notes'];
    const rows = entries.map(e => [
      e.id, e.email, e.name || '', e.profile_template_requested || '',
      e.origin, e.status, e.created_at, e.invited_at || '', (e.notes || '').replace(/\n/g, ' '),
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredCount = useMemo(() => entries.length, [entries]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
            <Inbox size={22} className="text-blue-600" />
            Waitlist bêta
          </h2>
          {stats && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatBadge label="En attente" count={stats.pending} color="amber" />
              <StatBadge label="Invités" count={stats.invited} color="blue" />
              <StatBadge label="Onboardés" count={stats.onboarded} color="green" />
              <StatBadge label="Déclinés" count={stats.declined} color="gray" />
              <span className="text-xs text-gray-400 ml-2">Total : {stats.total}</span>
            </div>
          )}
        </div>
        <button
          onClick={exportCsv}
          disabled={!entries.length}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${entries.length ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-50 text-gray-300 cursor-not-allowed'}`}
        >
          Exporter CSV
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Rechercher email ou nom..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <select value={originFilter} onChange={e => setOriginFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Toutes origines</option>
          {Object.entries(ORIGIN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="animate-spin w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full"></div></div>
      ) : filteredCount === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500 text-sm">
          Aucune entrée trouvée.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email · nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Template</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Origine</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Inscrit · invité</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const meta = STATUS_META[e.status] || { label: e.status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
                return (
                  <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{e.email}</div>
                      {e.name && <div className="text-xs text-gray-500">{e.name}</div>}
                      {e.notes && <div className="text-xs text-gray-400 italic mt-0.5 line-clamp-1" title={e.notes}>📝 {e.notes}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{e.profile_template_requested || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{ORIGIN_LABEL[e.origin] || e.origin}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <div>{fmtDate(e.created_at)}</div>
                      {e.invited_at && <div className="text-blue-600 mt-0.5">→ {fmtDate(e.invited_at)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {e.status === 'pending' && (
                        <button onClick={() => setStatus(e.id, 'invited')} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded transition-colors mr-1" title="Inviter">
                          <UserCheck size={16} />
                        </button>
                      )}
                      {e.status !== 'declined' && e.status !== 'onboarded' && (
                        <button onClick={() => setStatus(e.id, 'declined')} className="text-gray-500 hover:bg-gray-100 p-1.5 rounded transition-colors mr-1" title="Décliner">
                          <UserX size={16} />
                        </button>
                      )}
                      {e.status === 'invited' && (
                        <button onClick={() => setStatus(e.id, 'onboarded')} className="text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors mr-1" title="Marquer onboardé">
                          <Mail size={16} />
                        </button>
                      )}
                      <button onClick={() => setNotesModal({ entry: e, value: e.notes || '' })} className="text-gray-500 hover:bg-gray-100 p-1.5 rounded transition-colors mr-1" title="Notes">
                        <Edit3 size={16} />
                      </button>
                      <button onClick={() => remove(e)} className="text-red-500 hover:bg-red-50 p-1.5 rounded transition-colors" title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal notes */}
      {notesModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setNotesModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Notes — {notesModal.entry.email}</h3>
            <textarea
              value={notesModal.value}
              onChange={e => setNotesModal({ ...notesModal, value: e.target.value })}
              className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none resize-none"
              placeholder="Notes admin (visibles uniquement par les administrateurs)..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setNotesModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button
                onClick={async () => { await setStatus(notesModal.entry.id, notesModal.entry.status, notesModal.value); setNotesModal(null); }}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatBadge = ({ label, count, color }) => {
  const colorMap = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue:  'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    gray:  'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colorMap[color]}`}>
      {label} <strong>{count}</strong>
    </span>
  );
};

export default WaitlistPanel;
